import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Avatar,
  Menu,
  ListItemIcon,
  ListItemText,
  Alert,
  Snackbar,
  Tooltip,
  InputAdornment,
  Stack,
  CircularProgress,
  Grid,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormControlLabel,
  Divider,
  LinearProgress,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  Search as SearchIcon,
  Category as CategoryIcon,
  Clear as ClearIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  FirstPage as FirstPageIcon,
  LastPage as LastPageIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Visibility as VisibilityIcon,
  CloudUpload as CloudUploadIcon,
  FilterList as FilterIcon,
  Publish as PublishIcon,
  FilterAlt as FilterAltIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import OnOffSwitch from "../components/OnOffSwitch";
import adminModuleService from "../services/adminModuleService";
import ModuleCourseManager from "../components/ModuleCourseManager";
import { convertToSignedUrl } from "../utils/wasabiHelper";

function CourseManagement() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [courses, setCourses] = useState([]);
  const [filteredCourses, setFilteredCourses] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [statusAnchorEl, setStatusAnchorEl] = useState(null);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const [tempStatusFilter, setTempStatusFilter] = useState("all");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [courseToDelete, setCourseToDelete] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    publish: 0,
    inactive: 0,
  });

  // File upload states for thumbnail
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [thumbnailPreview, setThumbnailPreview] = useState("");
  const [uploadingThumbnail, setUploadingThumbnail] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const thumbnailInputRef = useRef(null);

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    thumbnail: "",
    tags: [],
    isActive: true,
    isComingSoon: false,
    notifyOnPublish: true,
    order: 1,
  });

  const [tagInput, setTagInput] = useState("");
  const [openModulesDialog, setOpenModulesDialog] = useState(false);
  const [selectedCourseForModules, setSelectedCourseForModules] =
    useState(null);

  const loadCourses = useCallback(async () => {
    try {
      setLoading(true);

      const params = {
        page: page + 1,
        limit: rowsPerPage,
        search: searchTerm,
        active: statusFilter,
        sortBy: "order",
        sortOrder: "asc",
      };

      const response = await adminModuleService.getCoursesSearch(params);
      const coursesData = response?.courses || [];
      const totalCount = response?.pagination?.total || 0;

      // Process courses to add signed URLs for thumbnails
      const processedCourses = coursesData.map((course) => ({
        ...course,
        thumbnail: course.thumbnail
          ? convertToSignedUrl(course.thumbnail)
          : course.thumbnail,
      }));

      setCourses(processedCourses);
      setFilteredCourses(processedCourses);

      // Calculate stats
      setStats((prev) => ({
        ...prev,
        total: totalCount,
        // publish and inactive counts are not returned by the search endpoint
      }));
    } catch (error) {
      console.error("Error loading courses:", error);
      setSnackbar({
        open: true,
        message: "Failed to load courses. Please try again.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  }, [searchTerm, statusFilter, page, rowsPerPage]);

  useEffect(() => {
    loadCourses();
  }, [loadCourses]);

  useEffect(() => {
    handleCloseStatusFilter();
  }, [statusFilter]);


  const clearFilters = () => {
    setSearchTerm("");
    setStatusFilter("all");
    setTempStatusFilter("all");
    setPage(0);
  };

  // Mobile Filter Dialog handlers
  const handleOpenMobileFilter = () => {
    setTempStatusFilter(statusFilter);
    setMobileFilterOpen(true);
  };

  const handleApplyMobileFilter = () => {
    setStatusFilter(tempStatusFilter);
    setPage(0);
    setMobileFilterOpen(false);
  };

  const handleClearMobileFilter = () => {
    setTempStatusFilter("all");
  };

  const handleOpenDialog = (course = null) => {
    if (course) {
      setEditingCourse(course);
      setFormData({
        name: course.name || "",
        description: course.description || "",
        thumbnail: course.thumbnail || "",
        tags: course.tags || [],
        isActive: course.isActive !== false,
        isComingSoon: course.isComingSoon === true,
        notifyOnPublish: course.notifyOnPublish !== false,
        order: course.order || 1,
      });
      setThumbnailPreview(course.thumbnail || "");
      setTagInput("");
    } else {
      setEditingCourse(null);
      setFormData({
        name: "",
        description: "",
        thumbnail: "",
        tags: [],
        isActive: true,
        isComingSoon: false,
        notifyOnPublish: true,
        order: Math.max(...courses.map((c) => c.order || 0), 0) + 1,
      });
      setThumbnailPreview("");
      setTagInput("");
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingCourse(null);
    setThumbnailFile(null);
    setThumbnailPreview("");
    setTagInput("");
  };

  const handleSaveCourse = async () => {
    try {
      setLoading(true);

      let thumbnailUrl = formData.thumbnail; // URL cũ nếu có

      // Kiểm tra nếu user đã xóa thumbnail
      if (!thumbnailPreview) {
        thumbnailUrl = null;
      }
      // Upload thumbnail if a new file is selected
      else if (thumbnailFile) {
        const formDataThumbnail = new FormData();
        formDataThumbnail.append("file", thumbnailFile);

        setUploadingThumbnail(true);
        try {
          const uploadResponse = await adminModuleService.uploadThumbnail(
            formDataThumbnail,
            (progress) => {
              setUploadProgress(
                Math.round((progress.loaded / progress.total) * 100)
              );
            }
          );

          // Lấy URL từ response
          thumbnailUrl = uploadResponse?.data?.url || uploadResponse?.url;
          // console.log('✅ Thumbnail uploaded, using signed URL for storage:', thumbnailUrl);
        } catch (uploadError) {
          console.error("❌ Error uploading thumbnail:", uploadError);
          setSnackbar({
            open: true,
            message: "Failed to upload thumbnail: " + uploadError.message,
            severity: "error",
          });
          return; // Dừng lại nếu upload thất bại
        } finally {
          setUploadingThumbnail(false);
          setUploadProgress(0);
        }
      } else {
        // Nếu không upload file mới, chuyển signed URL về URL gốc
        if (thumbnailUrl && thumbnailUrl.includes("AWSAccessKeyId")) {
          // Extract object key from signed URL
          const urlObj = new URL(thumbnailUrl);
          const pathParts = urlObj.pathname.split("/");
          pathParts.shift(); // Remove empty first element
          pathParts.shift(); // Remove bucket name
          const objectKey = pathParts.join("/");

          // Decode URL encoding (convert %2F back to /)
          const decodedObjectKey = decodeURIComponent(objectKey);

          // Tạo URL gốc từ object key đã decode
          thumbnailUrl = `https://s3.ap-southeast-2.wasabisys.com/airsmart/${decodedObjectKey}`;
          // console.log('✅ Converting signed URL to original URL for storage:', thumbnailUrl);
        }
      }

      // Tạo courseData với thumbnail URL
      const courseData = {
        name: formData.name,
        description: formData.description,
        thumbnail: thumbnailUrl, // Đảm bảo có thumbnail URL
        tags: formData.tags,
        isActive: formData.isActive,
        isComingSoon: formData.isComingSoon,
        notifyOnPublish: formData.notifyOnPublish,
        order: formData.order,
      };

      // console.log('🔄 Saving course with data:', courseData);

      if (editingCourse) {
        // Update existing course
        await adminModuleService.updateCourse(editingCourse.id, courseData);
        setSnackbar({
          open: true,
          message: "Course updated successfully!",
          severity: "success",
        });
      } else {
        // Add new course
        await adminModuleService.createCourse(courseData);
        setSnackbar({
          open: true,
          message: "Course created successfully!",
          severity: "success",
        });
      }
      handleCloseMenu(); // Đóng menu để tránh bị nhảy vị trí
      handleCloseDialog();
      loadCourses(); // Reload courses after save
    } catch (error) {
      console.error("Save error:", error);
      setSnackbar({
        open: true,
        message: error.message || "Failed to save course. Please try again.",
        severity: "error",
      });
    } finally {
      setLoading(false);
      setUploadingThumbnail(false);
      setUploadProgress(0);
    }
  };

  const handleOpenMenu = (event, course) => {
    setAnchorEl(event.currentTarget);
    setSelectedCourse(course);
  };

  const handleCloseMenu = (event) => {
    setAnchorEl(null);
    setSelectedCourse(null);
  };

  const handleOpenStatusFilter = (event) => {
    setStatusAnchorEl(event.currentTarget);
  };

  const handleCloseStatusFilter = () => {
    setStatusAnchorEl(null);
  };


  const exportCourses = () => {
    const csvContent = [
      [
        "ID",
        "Name",
        "Description",
        "Module Count",
        "Status",
        "Order",
        "Created At",
      ],
      ...filteredCourses.map((course) => [
        course.id,
        course.name || "",
        course.description || "",
        course.moduleCount || 0,
        course.isActive ? "Publish" : "Inactive",
        course.order || 0,
        course.createdAt || "",
      ]),
    ]
      .map((row) => row.join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `courses_${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString();
  };

  const handleOpenDeleteDialog = (course) => {
    setCourseToDelete(course);
    setOpenDeleteDialog(true);
    handleCloseMenu();
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setCourseToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!courseToDelete) return;

    try {
      await adminModuleService.deleteCourse(courseToDelete.id);
      setSnackbar({
        open: true,
        message: "Course deleted successfully!",
        severity: "info",
      });
      handleCloseMenu(); // Đóng menu để tránh bị nhảy vị trí
      handleCloseDeleteDialog();
      loadCourses();
    } catch (error) {
      console.error("Delete error:", error);
      setSnackbar({
        open: true,
        message: error.message || "Failed to delete course. Please try again.",
        severity: "error",
      });
    }
  };

  // Handle publish course
  const handlePublishCourse = async (course) => {
    try {
      console.log("Publishing course:", course.id, "with notifyOnPublish:", course.notifyOnPublish);
      const result = await adminModuleService.publishCourse(course.id);
      console.log("Publish result:", result);
      setSnackbar({
        open: true,
        message: "Course published successfully!",
        severity: "success",
      });
      handleCloseMenu();
      loadCourses();
    } catch (error) {
      console.error("Publish error:", error);
      setSnackbar({
        open: true,
        message: error.message || "Failed to publish course. Please try again.",
        severity: "error",
      });
    }
  };

  // Handle tag addition
  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData({
        ...formData,
        tags: [...formData.tags, tagInput.trim()],
      });
      setTagInput("");
    }
  };

  // Handle tag removal
  const handleRemoveTag = (tagToRemove) => {
    setFormData({
      ...formData,
      tags: formData.tags.filter((tag) => tag !== tagToRemove),
    });
  };

  const handleOpenModulesManager = (course) => {
    setSelectedCourseForModules(course);
    setOpenModulesDialog(true);
    handleCloseMenu();
  };

  const handleCloseModulesManager = () => {
    setOpenModulesDialog(false);
    setSelectedCourseForModules(null);
    handleCloseMenu(); // Đóng menu để tránh bị nhảy vị trí
    // No need to reload courses here - it will be triggered by onModulesChanged callback
    // when actual changes (delete/reorder) occur
  };

  return (
    <Box sx={{ pb: { xs: 3, md: 3 } }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "stretch", md: "center" },
          gap: { xs: 2, md: 0 },
          mb: { xs: 3, md: 4 },
        }}
      >
        <Box>
          <Typography
            variant="h4"
            sx={{ fontWeight: "bold", mb: 1, color: "primary.main" }}
          >
            Course Management
          </Typography>
        </Box>
        {/* Actions Buttons - Desktop Only */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          sx={{ width: { xs: "100%", md: "auto" }, display: { xs: "none", md: "flex" } }}
        >
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadCourses}
            disabled={loading}
            sx={{ borderRadius: 2, bgcolor: 'background.paper' }}
          >
            Refresh
          </Button>
          {/* <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={exportCourses}
            disabled={filteredCourses.length === 0}
            sx={{ borderRadius: 2, bgcolor: 'background.paper' }}
          >
            Export
          </Button> */}
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            sx={{ borderRadius: 2, px: 3 }}
          >
            Add Course
          </Button>
        </Stack>
      </Box>

      {/* Mobile Sticky Bottom Action Bar */}
      <Paper
        elevation={8}
        sx={{
          display: { xs: "flex", md: "none" },
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1100,
          p: 1.5,
          gap: 1,
          justifyContent: "center",
          alignItems: "center",
          borderTop: 1,
          borderColor: "divider",
          bgcolor: "background.paper",
        }}
      >
        <Button
          variant="outlined"
          size="small"
          startIcon={<RefreshIcon />}
          onClick={loadCourses}
          disabled={loading}
          sx={{ borderRadius: 2, flex: 1, maxWidth: 120 }}
        >
          Refresh
        </Button>
        {/* <Button
          variant="outlined"
          size="small"
          startIcon={<DownloadIcon />}
          onClick={exportCourses}
          disabled={filteredCourses.length === 0}
          sx={{ borderRadius: 2, flex: 1, maxWidth: 120 }}
        >
          Export
        </Button> */}
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          sx={{ borderRadius: 2, flex: 1, maxWidth: 120 }}
        >
          Add
        </Button>
      </Paper>

      {/* Filters */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 1, boxShadow: 2 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 1,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <FilterIcon color="primary" />
            <Typography
              variant="h6"
              sx={{ fontWeight: "bold" }}
              color="text.primary"
            >
              Filters & Search
            </Typography>
          </Box>

        </Box>

        {/* Main Filters Row */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            gap: { xs: 1, sm: 3 },
            alignItems: "center",
            mb: 3,
          }}
        >
          {/* Search Field */}
          <Box
            sx={{
              flex: { xs: "1 1 auto", sm: "1 1 40%", md: "1 1 50%" },
              minWidth: { xs: "auto", sm: "280px" },
            }}
          >
            <TextField
              placeholder="Search by name or description..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(0);
              }}
              size="medium"
              fullWidth
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 1,
                  backgroundColor: "action.hover",
                  height: 48,
                  "&:hover": {
                    backgroundColor: "action.selected",
                  },
                  "&.Mui-focused": {
                    backgroundColor: "background.paper",
                  },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {/* Mobile Filter Button */}
          <Box sx={{ display: { xs: "block", md: "none" } }}>
            <Button
              variant="outlined"
              onClick={handleOpenMobileFilter}
              sx={{
                height: 48,
                minWidth: 48,
                width: 48,
                borderRadius: 1,
                bgcolor: 'background.paper',
                borderColor: 'divider',
                p: 0,
                color: 'text.secondary'
              }}
            >
              <FilterAltIcon />
            </Button>
          </Box>

          {/* Pagination Controls Compact */}
          <Box
            sx={{
              flex: { xs: "1 1 100%", sm: "1 1 30%", md: "1 1 25%" },
              display: { xs: "none", md: "flex" },
              flexDirection: { xs: "column", sm: "row" },
              alignItems: { xs: "stretch", sm: "flex-start" },
              gap: { xs: 1, sm: 1.5 },
              justifyContent: { xs: "stretch", sm: "flex-end" },
            }}
          >
            {/* Per Page Selector */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                justifyContent: { xs: "space-between", sm: "flex-end" },
              }}
            >
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  fontWeight: "medium",
                  whiteSpace: "nowrap",
                  fontSize: "0.875rem",
                }}
              >
                Per page
              </Typography>

              <FormControl size="small" sx={{ minWidth: 70 }}>
                <Select
                  value={rowsPerPage}
                  onChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10));
                    setPage(0);
                  }}
                  sx={{
                    borderRadius: 1,
                    backgroundColor: "background.paper",
                    height: 36,
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "divider",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "primary.main",
                    },
                    "& .MuiSelect-select": {
                      py: 0.5,
                      px: 1,
                      fontSize: "0.875rem",
                      fontWeight: "medium",
                      color: "text.primary",
                    },
                  }}
                  displayEmpty
                  renderValue={(value) => value}
                >
                  <MenuItem value={5}>5</MenuItem>
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={25}>25</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
              {/* Navigation Controls */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  justifyContent: { xs: "center", sm: "flex-end" },
                }}
              >
                <Tooltip title="First page">
                  <span>
                    <IconButton
                      onClick={() => setPage(0)}
                      disabled={page === 0}
                      size="small"
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: 1,
                        color: "text.primary",
                        "&:hover": {
                          backgroundColor: "primary.light",
                          color: "primary.main",
                        },
                        "&.Mui-disabled": {
                          color: "text.disabled",
                        },
                      }}
                    >
                      <FirstPageIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>

                <Tooltip title="Previous page">
                  <span>
                    <IconButton
                      onClick={() => setPage(page - 1)}
                      disabled={page === 0}
                      size="small"
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: 1,
                        color: "text.primary",
                        "&:hover": {
                          backgroundColor: "primary.light",
                          color: "primary.main",
                        },
                        "&.Mui-disabled": {
                          color: "text.disabled",
                        },
                      }}
                    >
                      <ChevronLeftIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>

                <Box
                  sx={{
                    px: 1.5,
                    py: 0.5,
                    backgroundColor: "action.hover",
                    borderRadius: 1,
                    minWidth: { xs: 80, sm: 70 },
                    textAlign: "center",
                  }}
                >
                  <Typography
                    variant="body2"
                    color="text.primary"
                    sx={{
                      fontWeight: "medium",
                      fontSize: "0.75rem",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {page + 1} / {Math.ceil(stats.total / rowsPerPage) || 1}
                  </Typography>
                </Box>

                <Tooltip title="Next page">
                  <span>
                    <IconButton
                      onClick={() => setPage(page + 1)}
                      disabled={page >= Math.ceil(stats.total / rowsPerPage) - 1}
                      size="small"
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: 1,
                        color: "text.primary",
                        "&:hover": {
                          backgroundColor: "primary.light",
                          color: "primary.main",
                        },
                        "&.Mui-disabled": {
                          color: "text.disabled",
                        },
                      }}
                    >
                      <ChevronRightIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>

                <Tooltip title="Last page">
                  <span>
                    <IconButton
                      onClick={() =>
                        setPage(Math.ceil(stats.total / rowsPerPage) - 1)
                      }
                      disabled={page >= Math.ceil(stats.total / rowsPerPage) - 1}
                      size="small"
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: 1,
                        color: "text.primary",
                        "&:hover": {
                          backgroundColor: "primary.light",
                          color: "primary.main",
                        },
                        "&.Mui-disabled": {
                          color: "text.disabled",
                        },
                      }}
                    >
                      <LastPageIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>

              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  fontWeight: "medium",
                  fontSize: "0.875rem",
                  whiteSpace: "nowrap",
                  mt: 2,

                }}
              >
                Showing {Math.min(page * rowsPerPage + 1, stats.total)} -{" "}
                {Math.min((page + 1) * rowsPerPage, stats.total)} of {stats.total}{" "}
                courses
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Active Filters Chips */}
        {(searchTerm || statusFilter !== "all") && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: { xs: 0.5, sm: 1 },
              mt: 1,
              mb: 1
            }}
          >
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mr: { xs: 0.5, sm: 1 },
                display: { xs: 'none', sm: 'block' }
              }}
            >
              Active Filters:
            </Typography>

            {searchTerm && (
              <Chip
                label={searchTerm}
                onDelete={() => {
                  setSearchTerm("");
                  setPage(0);
                }}
                color="primary"
                variant="outlined"
                size="small"
                sx={{
                  maxWidth: { xs: 120, sm: 200 },
                  '& .MuiChip-label': {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }
                }}
              />
            )}

            {statusFilter !== "all" && (
              <Chip
                label={statusFilter === 'publish' ? 'Published' : 'Inactive'}
                onDelete={() => {
                  setStatusFilter("all");
                  setPage(0);
                }}
                color="primary"
                variant="outlined"
                size="small"
              />
            )}

            <Button
              size="small"
              onClick={clearFilters}
              startIcon={<ClearIcon />}
              variant="outlined"
              color="warning"
              sx={{
                borderRadius: 2,
                whiteSpace: "nowrap",
                px: { xs: 1, sm: 2 },
                minWidth: 'auto',
                "&:hover": {
                  backgroundColor: "warning.light",
                },
              }}
            >
              <Box sx={{ display: { xs: 'none', sm: 'block' } }}>CLEAR ALL</Box>
              <Box sx={{ display: { xs: 'block', sm: 'none' } }}>Clear</Box>
            </Button>
          </Box>
        )}

        {/* Mobile Pagination - Compact Single Row */}
        <Box
          sx={{
            display: { xs: "flex", md: "none" },
            alignItems: "center",
            justifyContent: "space-between",
            pt: 1,
            mt: 1,
            borderTop: 1,
            borderColor: "divider"
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <FormControl size="small">
              <Select
                value={rowsPerPage}
                onChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                sx={{ height: 28, fontSize: '0.75rem', minWidth: 50 }}
              >
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={25}>25</MenuItem>
                <MenuItem value={50}>50</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 0 }}>
            <IconButton
              onClick={() => setPage(0)}
              disabled={page === 0}
              size="small"
              sx={{ p: 0.5 }}
            >
              <FirstPageIcon fontSize="small" />
            </IconButton>
            <IconButton
              onClick={() => setPage(page - 1)}
              disabled={page === 0}
              size="small"
              sx={{ p: 0.5 }}
            >
              <ChevronLeftIcon fontSize="small" />
            </IconButton>

            <Typography variant="caption" sx={{ mx: 0.5, fontWeight: "medium", minWidth: 40, textAlign: 'center' }}>
              {page + 1}/{Math.max(1, Math.ceil(stats.total / rowsPerPage))}
            </Typography>

            <IconButton
              onClick={() => setPage(page + 1)}
              disabled={page >= Math.ceil(stats.total / rowsPerPage) - 1}
              size="small"
              sx={{ p: 0.5 }}
            >
              <ChevronRightIcon fontSize="small" />
            </IconButton>
            <IconButton
              onClick={() => setPage(Math.max(0, Math.ceil(stats.total / rowsPerPage) - 1))}
              disabled={page >= Math.ceil(stats.total / rowsPerPage) - 1}
              size="small"
              sx={{ p: 0.5 }}
            >
              <LastPageIcon fontSize="small" />
            </IconButton>
          </Box>

          <Typography variant="caption" color="text.secondary" sx={{ minWidth: 70, textAlign: 'right' }}>
            {stats.total} courses
          </Typography>
        </Box>
      </Paper>

      {/* Courses - Conditional Mobile/Desktop View */}
      {isMobile ? (
        /* Mobile Card View */
        <Stack spacing={1}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : filteredCourses.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <CategoryIcon sx={{ fontSize: 48, color: "text.disabled", mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                No courses found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {searchTerm || statusFilter !== "all"
                  ? "Try adjusting your search filters"
                  : "Start by adding your first course"}
              </Typography>
            </Box>
          ) : (
            filteredCourses
              .map((course) => (
                <Paper
                  key={course.id}
                  sx={{
                    p: 2,
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                    position: 'relative'
                  }}
                >
                  {/* Course Header */}
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                    <Avatar
                      sx={{
                        bgcolor: course.isActive ? "primary.main" : "grey.500",
                        color: "white",
                        width: 48,
                        height: 48
                      }}
                    >
                      {(course.name || "C").charAt(0).toUpperCase()}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="subtitle1" fontWeight="medium" noWrap>
                        {course.name || "Unnamed Course"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        ID: {course.id?.substring(0, 8)}...
                      </Typography>
                    </Box>
                    <IconButton
                      onClick={(e) => handleOpenMenu(e, course)}
                      size="small"
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </Box>

                  {/* Description */}
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      mb: 2,
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden'
                    }}
                  >
                    {course.description || "No description"}
                  </Typography>

                  <Divider sx={{ my: 1.5 }} />

                  {/* Course Info Grid */}
                  <Grid container spacing={1} sx={{ mb: 1.5 }}>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Modules</Typography>
                      <Box>
                        <Chip
                          label={`${course.moduleCount || 0} modules`}
                          size="small"
                          color={course.moduleCount > 0 ? "primary" : "default"}
                          variant="outlined"
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Status</Typography>
                      <Box>
                        <Chip
                          label={course.isActive ? "Publish" : "Inactive"}
                          color={course.isActive ? "success" : "default"}
                          size="small"
                          variant="filled"
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Access</Typography>
                      <Box>
                        <Chip
                          label={course.isComingSoon ? "Coming Soon" : "Unlocked"}
                          color={course.isComingSoon ? "error" : "default"}
                          size="small"
                          variant="filled"
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Order</Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {course.order || 0}
                      </Typography>
                    </Grid>
                  </Grid>

                  <Typography variant="caption" color="text.secondary">
                    Created: {formatDate(course.createdAt)}
                  </Typography>
                </Paper>
              ))
          )}
        </Stack>
      ) : (
        /* Desktop Table View */
        <TableContainer component={Paper} sx={{ borderRadius: 1, boxShadow: 2 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: "action.hover" }}>
                <TableCell sx={{ fontWeight: "bold", fontSize: "0.875rem", width: "20%" }}>
                  Course
                </TableCell>
                <TableCell sx={{ fontWeight: "bold", fontSize: "0.875rem", width: "25%" }}>
                  Description
                </TableCell>
                <TableCell sx={{ fontWeight: "bold", fontSize: "0.875rem", width: "10%" }}>
                  Modules
                </TableCell>
                <TableCell sx={{ fontWeight: "bold", fontSize: "0.875rem", width: "10%" }}>
                  Status
                  <IconButton size="small" onClick={handleOpenStatusFilter}>
                    <FilterIcon fontSize="small" />
                  </IconButton>
                  <Menu
                    anchorEl={statusAnchorEl}
                    open={Boolean(statusAnchorEl)}
                    onClose={handleCloseStatusFilter}
                  >
                    <MenuItem
                      onClick={() => {
                        setStatusFilter("all");
                        setPage(0);
                      }}
                      selected={statusFilter === "all"}
                    >
                      All Courses
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        setStatusFilter("publish");
                        setPage(0);
                      }}
                      selected={statusFilter === "publish"}
                    >
                      Publish Only
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        setStatusFilter("inactive");
                        setPage(0);
                      }}
                      selected={statusFilter === "inactive"}
                    >
                      Inactive Only
                    </MenuItem>
                  </Menu>
                </TableCell>
                <TableCell sx={{ fontWeight: "bold", fontSize: "0.875rem", width: "10%" }}>
                  Access
                </TableCell>
                <TableCell sx={{ fontWeight: "bold", fontSize: "0.875rem", width: "5%" }}>
                  Order
                </TableCell>
                <TableCell sx={{ fontWeight: "bold", fontSize: "0.875rem", width: "10%" }}>
                  Created
                </TableCell>
                <TableCell
                  align="center"
                  sx={{ fontWeight: "bold", fontSize: "0.875rem", width: "10%" }}
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} sx={{ textAlign: "center", py: 4 }}>
                    <CircularProgress />
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 2 }}
                    >
                      Loading courses...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : filteredCourses.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} sx={{ textAlign: "center", py: 4 }}>
                    <CategoryIcon
                      sx={{ fontSize: 48, color: "text.disabled", mb: 2 }}
                    />
                    <Typography variant="h6" color="text.secondary">
                      No courses found
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {searchTerm || statusFilter !== "all"
                        ? "Try adjusting your search filters"
                        : "Start by adding your first course"}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredCourses
                  .map((course) => (
                    <TableRow
                      key={course.id}
                      hover
                      sx={{
                        "&:hover": {
                          backgroundColor: "action.hover",
                        },
                      }}
                    >
                      <TableCell>
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 2 }}
                        >
                          <Avatar
                            sx={{
                              bgcolor: course.isActive
                                ? "primary.main"
                                : "grey.500",
                              color: "white",
                            }}
                          >
                            {(course.name || "C").charAt(0).toUpperCase()}
                          </Avatar>
                          <Box>
                            <Typography
                              variant="body1"
                              sx={{ fontWeight: "medium" }}
                              color="text.primary"
                            >
                              {course.name || "Unnamed Course"}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              ID: {course.id?.substring(0, 8)}...
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          color="text.primary"
                          sx={{
                            maxWidth: 200,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {course.description || "No description"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={`${course.moduleCount || 0} modules`}
                          size="small"
                          color={course.moduleCount > 0 ? "primary" : "default"}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={course.isActive ? "Publish" : "Inactive"}
                          color={course.isActive ? "success" : "default"}
                          size="small"
                          variant="filled"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={course.isComingSoon ? "Coming Soon" : "Unlocked"}
                          color={course.isComingSoon ? "error" : "default"}
                          size="small"
                          variant="filled"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.primary">
                          {course.order || 0}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.primary">
                          {formatDate(course.createdAt)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="More actions">
                          <IconButton
                            onClick={(e) => handleOpenMenu(e, course)}
                            size="small"
                            sx={{
                              "&:hover": {
                                backgroundColor: "action.hover",
                                color: "primary.main",
                              },
                            }}
                          >
                            <MoreVertIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
      >
        <MenuItem onClick={() => handleOpenDialog(selectedCourse)}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit Course</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => handleOpenModulesManager(selectedCourse)}>
          <ListItemIcon>
            <VisibilityIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>
            <Box>
              <Typography variant="body2">
                Manage Modules ({selectedCourse?.moduleCount || 0})
              </Typography>
              <Typography variant="caption" color="text.secondary">
                View, reorder and delete modules
              </Typography>
            </Box>
          </ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => handleOpenDeleteDialog(selectedCourse)}
          sx={{ color: "error.main" }}
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Delete Course</ListItemText>
        </MenuItem>
      </Menu>

      {/* Add/Edit Course Dialog */}
      <Dialog
        sx={{ mb: 10 }}
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxHeight: "90vh",
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" fontWeight="bold">
            {editingCourse ? "Edit Course" : "Add New Course"}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Course Name"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              fullWidth
              required
            />
            <TextField
              label="Description"
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              multiline
              rows={3}
              fullWidth
              required
            />

            {/* Thumbnail Upload */}
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Thumbnail (Image URL or Upload)
              </Typography>
              <Button
                variant="outlined"
                component="label"
                size="small"
                startIcon={<CloudUploadIcon />}
                sx={{
                  borderRadius: 2,
                  width: "100%",
                  justifyContent: "flex-start",
                  ...(thumbnailFile && {
                    backgroundColor: "action.selected",
                    borderColor: "primary.main",
                    color: "primary.main",
                  }),
                }}
              >
                {thumbnailFile ? "Change Thumbnail" : "Upload Thumbnail"}
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      setThumbnailFile(file);
                      // Clear preview trước khi set lại để tránh cache issue
                      setThumbnailPreview(null);
                      setTimeout(() => {
                        setThumbnailPreview(URL.createObjectURL(file));
                      }, 0);
                    }
                    // Reset input để có thể chọn lại cùng file
                    e.target.value = '';
                  }}
                  hidden
                  ref={thumbnailInputRef}
                />
              </Button>

              {thumbnailPreview && (
                <Box sx={{ mt: 2, position: "relative" }}>
                  <img
                    src={thumbnailPreview}
                    alt="Thumbnail Preview"
                    style={{
                      width: "100%",
                      height: "auto",
                      borderRadius: 8,
                      border: "1px solid",
                      borderColor: "action.hover",
                    }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => {
                      setThumbnailFile(null);
                      setThumbnailPreview("");
                      setFormData({ ...formData, thumbnail: "" });
                    }}
                    sx={{
                      position: "absolute",
                      top: 8,
                      right: 8,
                      bgcolor: "background.paper",
                      "&:hover": {
                        bgcolor: "error.light",
                      },
                    }}
                  >
                    <ClearIcon color="error" />
                  </IconButton>
                </Box>
              )}

              {uploadingThumbnail && (
                <Box sx={{ mt: 1 }}>
                  <LinearProgress
                    variant="determinate"
                    value={uploadProgress}
                  />
                </Box>
              )}
            </Box>

            {/* Tags Section */}
            <Box>
              <Typography variant="h6" gutterBottom>
                Tags (Optional)
              </Typography>

              <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                <TextField
                  label="Add Tags"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleAddTag()}
                  fullWidth
                  variant="outlined"
                  placeholder="Enter tag and press Enter"
                  helperText="Add relevant keywords to help users find this course"
                />
                <Button
                  onClick={handleAddTag}
                  disabled={!tagInput.trim()}
                  variant="outlined"
                  startIcon={<AddIcon />}
                  sx={{ minWidth: "120px" }}
                >
                  Add Tag
                </Button>
              </Stack>

              {formData.tags.length > 0 && (
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1,
                    p: 2,
                    bgcolor: "grey.50",
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: "grey.300",
                  }}
                >
                  {formData.tags.map((tag, index) => (
                    <Chip
                      key={index}
                      label={tag}
                      onDelete={() => handleRemoveTag(tag)}
                      color="primary"
                      variant="outlined"
                    />
                  ))}
                </Box>
              )}
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  label="Order"
                  type="number"
                  value={formData.order}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      order: parseInt(e.target.value) || 1,
                    })
                  }
                  fullWidth
                  inputProps={{ min: 1 }}
                />
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                  <FormControlLabel
                    control={
                      <OnOffSwitch
                        checked={formData.isActive}
                        onChange={(e) =>
                          setFormData({ ...formData, isActive: e.target.checked })
                        }
                      />
                    }
                    label="Publish"
                    sx={{ mt: 1 }}
                  />
                  <FormControlLabel
                    control={
                      <OnOffSwitch
                        checked={formData.notifyOnPublish}
                        disabled={!!editingCourse}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            notifyOnPublish: e.target.checked,
                          })
                        }
                      />
                    }
                    label="Notify Users"
                    sx={{ mt: 1 }}
                  />
                  <FormControlLabel
                    control={
                      <OnOffSwitch
                        checked={formData.isComingSoon}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            isComingSoon: e.target.checked,
                          })
                        }
                      />
                    }
                    label="Coming Soon"
                    sx={{ mt: 1 }}
                  />
                </Box>
              </Grid>
            </Grid>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSaveCourse}
            variant="contained"
            disabled={!formData.name || !formData.description}
          >
            {editingCourse ? "Update Course" : "Create Course"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={handleCloseDeleteDialog}
        aria-labelledby="delete-confirmation-dialog"
      >
        <DialogTitle id="delete-confirmation-dialog">
          Confirm Deletion
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Are you sure you want to delete the course "{courseToDelete?.name}"?
          </Typography>
          {courseToDelete?.moduleCount > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              This course contains {courseToDelete.moduleCount} modules.
            </Alert>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
          >
            Delete Course
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Module Course Manager Dialog */}
      <Dialog
        open={openModulesDialog}
        onClose={handleCloseModulesManager}
        maxWidth={false}
        fullWidth
        sx={{
          "& .MuiDialog-paper": {
            width: "90vw",
            height: "85vh",
            maxWidth: "none",
            maxHeight: "none",
            borderRadius: 1,
          },
        }}
      >
        <DialogContent sx={{ p: 0, height: "100%" }}>
          {selectedCourseForModules && (
            <ModuleCourseManager
              courseId={selectedCourseForModules.id}
              courseName={selectedCourseForModules.name}
              onClose={handleCloseModulesManager}
              onModulesChanged={loadCourses}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Mobile Filter Dialog */}
      <Dialog
        open={mobileFilterOpen}
        onClose={() => setMobileFilterOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FilterIcon color="primary" />
            <Typography variant="h6" fontWeight="bold">
              Filters
            </Typography>
          </Box>
          <IconButton onClick={() => setMobileFilterOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            {/* Status Filter */}
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={tempStatusFilter}
                label="Status"
                onChange={(e) => setTempStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All Courses</MenuItem>
                <MenuItem value="publish">Published Only</MenuItem>
                <MenuItem value="inactive">Inactive Only</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClearMobileFilter} color="warning">
            Clear All
          </Button>
          <Button onClick={handleApplyMobileFilter} variant="contained">
            Apply
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default CourseManagement;
